import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import FieldLayout from '@/components/Layout/FieldLayout';
import Button from '@/components/ui/Button';
import Label from '@/components/ui/Label';

import PrimaryInput from '@/components/ui/PrimaryInput';
import {
  IAddEmployeeForm,
  IEmployeeOnBoardResponse,
} from '@/interfaces/employee.model';
import { IPreviousWork } from '@/interfaces/user.model';
import { showToastNotification } from '@/utility/helper';
import { errorTypes } from '@/utility/validation';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

interface IEmployeePreviousWorkStep7 {
  nextStep: () => void;
  empId: string | null;
  previousWork?: IPreviousWork[];
}
const EmployeePreviousWorkStep7 = ({
  nextStep,
  empId,
  previousWork,
}: IEmployeePreviousWorkStep7) => {
  const [currentCompanyStates, setCurrentCompanyStates] = useState<
    Record<number, boolean>
  >({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    control,
    register,
    watch,
    setValue,
    clearErrors,
    getValues,
    formState: { errors },
  } = useFormContext<IAddEmployeeForm>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'previousWork',
  });
  const handleCheckboxChange = (index: number, isChecked: boolean) => {
    const updatedStates = { ...currentCompanyStates, [index]: isChecked };
    setCurrentCompanyStates(updatedStates);

    if (isChecked) {
      setValue(`previousWork.${index}.toDate`, '');
      clearErrors(`previousWork.${index}.toDate`);
    }
  };
  const handleNextStep = async () => {
    const data = getValues();
    if (data?.previousWork?.length > 0) {
      setIsLoading(true);
      const previousWork = data?.previousWork?.map((work) => ({
        ...work,
        toDate: work.toDate === '' ? null : work.toDate,
      }));
      const submitData = { previousWork };
      try {
        const response: IEmployeeOnBoardResponse = await callApi(
          API_URL + API_ENDPOINT.addPreviousWork + `?userId=${empId}`,
          { method: 'POST', data: submitData },
        );
        if (response?.status) {
          showToastNotification('success', response?.message);
          nextStep();
        }
      } catch (error) {
        if (error && typeof error === 'object' && 'message' in error) {
          showToastNotification('danger', (error as IApiErrorResponse).message);
        } else {
          showToastNotification('danger', 'Something went wrong');
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      nextStep();
    }
  };
  useEffect(() => {
    if (previousWork) {
      const mappedData = previousWork?.map((work, index) => {
        if (work.toDate === null) {
          setCurrentCompanyStates((prevState) => ({
            ...prevState,
            [index]: true,
          }));
        }
        return {
          ...work,
          fromDate: work.fromDate
            ? new Date(work.fromDate).toISOString().split('T')[0]
            : '',
          toDate: work.toDate
            ? new Date(work.toDate).toISOString().split('T')[0]
            : moment().format('YYYY-MM-DD'),
        };
      });
      append(mappedData);
    }
  }, [previousWork]);

  return (
    <>
      <div className="row">
        <div className="col-12">
          <h4 className="fs-3 rajdhani-semibold my-4 border-bottom pb-3">
            Previous Work
          </h4>
        </div>
        {fields.length > 0 ? (
          fields.map((field, index) => (
            <div className="col-12 mb-3" key={field.id}>
              <div className="card border">
                <div className="card-body">
                  <div className="row">
                    <div className="col-12 d-flex justify-content-end">
                      <Button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => remove(index)}
                      >
                        Remove
                      </Button>
                    </div>
                    <FieldLayout fieldStyle="col-lg-4">
                      <PrimaryInput
                        errors={
                          errors?.previousWork?.[index]?.companyName?.message
                        }
                        required={true}
                        label="Company Name"
                        id={`companyName-${index}`}
                        placeholder="Enter company name"
                        {...register(`previousWork.${index}.companyName`, {
                          ...errorTypes.required('Company Name'),
                        })}
                      />
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-lg-4">
                      <PrimaryInput
                        errors={
                          errors?.previousWork?.[index]?.companyName?.message
                        }
                        required={true}
                        label="Designation Name"
                        id={`designationName-${index}`}
                        placeholder="Enter designation name"
                        {...register(`previousWork.${index}.designationName`, {
                          required: 'This field is Required',
                        })}
                      />
                      {errors?.previousWork?.[index]?.designationName && (
                        <div className="form-text text-danger">
                          {errors.previousWork[index].designationName.message}
                        </div>
                      )}
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-lg-4">
                      <PrimaryInput
                        errors={
                          errors?.previousWork?.[index]?.companyName?.message
                        }
                        type="date"
                        required={true}
                        label="From "
                        id={`fromDate-${index}`}
                        max={new Date().toISOString().split('T')[0]}
                        {...register(`previousWork.${index}.fromDate`, {
                          required: 'This field is Required',
                        })}
                      />

                      {errors?.previousWork?.[index]?.fromDate && (
                        <div className="form-text text-danger">
                          {errors.previousWork[index].fromDate.message}
                        </div>
                      )}
                    </FieldLayout>
                    <FieldLayout fieldStyle="col-lg-4">
                      <PrimaryInput
                        errors={
                          errors?.previousWork?.[index]?.companyName?.message
                        }
                        type="date"
                        required={true}
                        label="To"
                        id={`toDate-${index}`}
                        inputMode="none"
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        value={watch(`previousWork.${index}.toDate`) || ''}
                        min={watch(`previousWork.${index}.fromDate`)}
                        max={new Date().toISOString().split('T')[0]}
                        {...register(`previousWork.${index}.toDate`, {
                          required: !currentCompanyStates[index]
                            ? 'This field is Required'
                            : false,
                          onChange: (e) => {
                            const from = e.target.value;
                            const to = getValues(
                              `previousWork.${index}.toDate`,
                            );
                            if (from && to && from > to) {
                              setValue(`previousWork.${index}.toDate`, '', {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                            }
                          },
                        })}
                        disabled={currentCompanyStates[index]}
                      />
                      {/* <PrimaryInput
                        type="date"
                        label="From"
                        id={`fromDate-${index}`}
                        max={new Date().toISOString().split('T')[0]}
                        {...register(`previousWork.${index}.fromDate`, {
                          onChange: (e) => {
                            const from = e.target.value;
                            const to = getValues(
                              `previousWork.${index}.toDate`,
                            );

                            console.log('FROM:', from, 'TO:', to);

                            if (from && to && from > to) {
                              setValue(`previousWork.${index}.toDate`, '', {
                                shouldDirty: true,
                                shouldTouch: true,
                              });
                            }
                          },
                        })}
                      /> */}

                      {errors?.previousWork?.[index]?.toDate && (
                        <div className="form-text text-danger">
                          {errors.previousWork[index].toDate.message}
                        </div>
                      )}
                    </FieldLayout>
                    <div className="col-12 col-md-6 col-lg-4 align-self-end">
                      <div className="form-check form-group mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`current-${index}`}
                          checked={
                            currentCompanyStates
                              ? !!currentCompanyStates[index]
                              : false
                          }
                          onChange={(e) =>
                            handleCheckboxChange(index, e.target.checked)
                          }
                        />
                        <Label
                          labelClassname="form-check-label"
                          id={`current-${index}`}
                        >
                          Current Company
                        </Label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="d-flex justify-content-center my-4 text-muted">
            <h3 className="border rounded p-3">No Previous Work</h3>
          </div>
        )}
      </div>
      <div className="d-flex align-items-center justify-content-between gap-4 mt-4">
        <Button
          type="button"
          className="btn btn-primary px-5"
          onClick={() =>
            append({
              companyName: '',
              designationName: '',
              fromDate: '',
              toDate: '',
            })
          }
        >
          {fields?.length > 0 ? 'Add More' : 'Add'}
        </Button>
        <Button
          className="btn btn-primary px-4"
          type="button"
          onClick={handleNextStep}
          isLoading={isLoading}
        >
          Save and Continue
        </Button>
      </div>
    </>
  );
};
export default EmployeePreviousWorkStep7;
